import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import {
  ChevronLeft,
  ChevronRight,
  Undo2,
  Redo2,
  Grid3x3,
  Copy,
  ClipboardPaste,
  Trash2,
  AlignLeft,
  AlignRight,
  AlignStartVertical,
  AlignEndVertical,
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  ArrowLeftRight,
  ArrowUpDown,
  Globe,
} from 'lucide-react';

interface DesignerToolbarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  scale: number;
  onScaleChange: (scale: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  snapToGrid: boolean;
  onToggleGrid: () => void;
  gridSize: number;
  onGridSizeChange: (size: number) => void;
  selectedCount: number;
  clipboardCount: number;
  onCopy: () => void;
  onPaste: () => void;
  onDelete: () => void;
  onAlignLeft: () => void;
  onAlignRight: () => void;
  onAlignTop: () => void;
  onAlignBottom: () => void;
  onAlignCenterH: () => void;
  onAlignCenterV: () => void;
  onDistributeH: () => void;
  onDistributeV: () => void;
  onToggleGlobal: () => void;
}

export function DesignerToolbar({
  currentPage,
  totalPages,
  onPageChange,
  scale,
  onScaleChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  snapToGrid,
  onToggleGrid,
  gridSize,
  onGridSizeChange,
  selectedCount,
  clipboardCount,
  onCopy,
  onPaste,
  onDelete,
  onAlignLeft,
  onAlignRight,
  onAlignTop,
  onAlignBottom,
  onAlignCenterH,
  onAlignCenterV,
  onDistributeH,
  onDistributeV,
  onToggleGlobal,
}: DesignerToolbarProps) {
  return (
    <div className="h-12 border-t bg-card px-4 flex items-center gap-2 flex-shrink-0">
      <TooltipProvider delayDuration={300}>
        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Vorige pagina</TooltipContent>
          </Tooltip>
          <span className="text-sm px-2 min-w-[60px] text-center">
            {currentPage} / {totalPages}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Volgende pagina</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Zoom */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onScaleChange(Math.max(0.5, scale - 0.1))}
          >
            -
          </Button>
          <Select
            value={Math.round(scale * 100).toString()}
            onValueChange={(v) => onScaleChange(Number(v) / 100)}
          >
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50%</SelectItem>
              <SelectItem value="75">75%</SelectItem>
              <SelectItem value="100">100%</SelectItem>
              <SelectItem value="125">125%</SelectItem>
              <SelectItem value="150">150%</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onScaleChange(Math.min(2, scale + 0.1))}
          >
            +
          </Button>
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Undo/Redo */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={onUndo}
                disabled={!canUndo}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Ongedaan maken (Ctrl+Z)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={onRedo}
                disabled={!canRedo}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Opnieuw (Ctrl+Shift+Z)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Grid */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={snapToGrid ? 'default' : 'outline'}
                size="icon"
                className="h-8 w-8"
                onClick={onToggleGrid}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Grid aan/uit (G)</TooltipContent>
          </Tooltip>
          {snapToGrid && (
            <Select
              value={gridSize.toString()}
              onValueChange={(v) => onGridSizeChange(Number(v))}
            >
              <SelectTrigger className="w-16 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5px</SelectItem>
                <SelectItem value="10">10px</SelectItem>
                <SelectItem value="15">15px</SelectItem>
                <SelectItem value="20">20px</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Clipboard */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={onCopy}
                disabled={selectedCount === 0}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Kopiëren (Ctrl+C)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={onPaste}
                disabled={clipboardCount === 0}
              >
                <ClipboardPaste className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Plakken (Ctrl+V)</TooltipContent>
          </Tooltip>
        </div>

        {/* Selection info and actions (when items selected) */}
        {selectedCount > 0 && (
          <>
            <Separator orientation="vertical" className="h-6" />
            
            <span className="text-sm text-muted-foreground px-2">
              {selectedCount} geselecteerd
            </span>

            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={onAlignLeft}>
                    <AlignLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Links uitlijnen (Alt+L)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={onAlignCenterH}>
                    <AlignHorizontalJustifyCenter className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Horizontaal centreren</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={onAlignRight}>
                    <AlignRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Rechts uitlijnen (Alt+R)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={onAlignTop}>
                    <AlignStartVertical className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Boven uitlijnen (Alt+T)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={onAlignCenterV}>
                    <AlignVerticalJustifyCenter className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Verticaal centreren</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={onAlignBottom}>
                    <AlignEndVertical className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Onder uitlijnen (Alt+B)</TooltipContent>
              </Tooltip>
            </div>

            {selectedCount >= 3 && (
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={onDistributeH}>
                      <ArrowLeftRight className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Horizontaal verdelen</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={onDistributeV}>
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Verticaal verdelen</TooltipContent>
                </Tooltip>
              </div>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={onToggleGlobal}>
                  <Globe className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Globaal (alle pagina's)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={onDelete}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Verwijderen (Delete)</TooltipContent>
            </Tooltip>
          </>
        )}
      </TooltipProvider>
    </div>
  );
}
